import React, { useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
  Loader2,
  Wand2,
  X,
} from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useCreateShift,
  useDeleteShift,
  useGenerateShiftsFromSchedules,
  useShifts,
  useUpdateShift,
  type GenerateShiftsResult,
  type ShiftCreateInput,
  type ShiftDefinition,
} from '@/hooks/optimization/useOptimizationSettings';
import { usePositions } from '@/hooks/usePositions';

interface ShiftsTabProps {
  locationId: string | null;
}

export const ShiftsTab: React.FC<ShiftsTabProps> = ({ locationId }) => {
  const { data: shifts, isLoading } = useShifts(locationId);
  const del = useDeleteShift();
  const generate = useGenerateShiftsFromSchedules(locationId);
  const [editing, setEditing] = useState<ShiftDefinition | null>(null);
  const [creating, setCreating] = useState(false);
  const [genResult, setGenResult] = useState<GenerateShiftsResult | null>(
    null,
  );
  const [genError, setGenError] = useState('');

  if (!locationId) {
    return (
      <p className="text-sm text-muted-foreground">
        Выберите локацию, чтобы увидеть смены.
      </p>
    );
  }

  const runGenerate = async () => {
    setGenError('');
    setGenResult(null);
    try {
      setGenResult(await generate.mutateAsync());
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Ошибка генерации');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center gap-2">
        <p className="text-sm text-muted-foreground">
          Каждая смена привязана к локации. Допустимые позиции — кто может
          работать в эту смену.
        </p>
        <div className="flex gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={runGenerate}
            disabled={generate.isPending}
            title="Извлечь смены из назначенных графиков сотрудников этой локации"
          >
            {generate.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Wand2 className="h-4 w-4 mr-2" />
            )}
            Из графиков сотрудников
          </Button>
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Добавить смену
          </Button>
        </div>
      </div>

      {genError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{genError}</AlertDescription>
        </Alert>
      )}
      {genResult && (
        <Alert>
          <AlertDescription>
            <div className="flex justify-between items-start gap-2">
              <div className="space-y-1 text-sm">
                <p>
                  Проанализировано графиков сотрудников:{' '}
                  <b>{genResult.employees_analyzed}</b>. Создано смен:{' '}
                  <b>{genResult.created.length}</b>
                  {genResult.created.length > 0 && (
                    <>
                      {' '}
                      (
                      {genResult.created
                        .map((s) => `${s.start_time}–${s.end_time}`)
                        .join(', ')}
                      )
                    </>
                  )}
                  .
                </p>
                {genResult.skipped_existing.length > 0 && (
                  <p className="text-muted-foreground">
                    Уже существуют:{' '}
                    {genResult.skipped_existing
                      .map((s) => `${s.start_time}–${s.end_time}`)
                      .join(', ')}
                  </p>
                )}
                {genResult.skipped_too_long.length > 0 && (
                  <p className="text-amber-700">
                    Пропущены как слишком длинные (лимит{' '}
                    {genResult.max_shift_hours} ч/день — солвер не сможет их
                    назначить):{' '}
                    {genResult.skipped_too_long
                      .map(
                        (s) =>
                          `${s.start_time}–${s.end_time} (${s.duration_hours} ч, ${s.employees} чел)`,
                      )
                      .join(', ')}
                  </p>
                )}
                {genResult.created.length === 0 &&
                  genResult.skipped_existing.length === 0 &&
                  genResult.skipped_too_long.length === 0 && (
                    <p className="text-muted-foreground">
                      В назначенных графиках не нашлось интервалов вида
                      «08:00-20:00» (или у каждого интервала меньше двух
                      сотрудников).
                    </p>
                  )}
              </div>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setGenResult(null)}
                aria-label="Скрыть"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !shifts || shifts.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          Смены пока не настроены.
        </p>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Код</TableHead>
                <TableHead>Название</TableHead>
                <TableHead>Время</TableHead>
                <TableHead className="text-right">Часов</TableHead>
                <TableHead className="text-right">Должности</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shifts.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-mono">{s.code}</TableCell>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>
                    {s.start_time}–{s.end_time}
                  </TableCell>
                  <TableCell className="text-right">
                    {Number(s.duration_hours).toFixed(1)}
                  </TableCell>
                  <TableCell className="text-right">
                    {s.position_ids.length || '—'}
                  </TableCell>
                  <TableCell>
                    {s.is_active ? (
                      <span className="text-green-700 text-xs">Активна</span>
                    ) : (
                      <span className="text-muted-foreground text-xs">
                        Архив
                      </span>
                    )}
                    {s.source === 'auto' && (
                      <span
                        className="ml-2 rounded bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.5 align-middle"
                        title="Смена сгенерирована автоматически — проверьте часы и сохраните, чтобы подтвердить"
                      >
                        авто
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing(s)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Удалить смену "${s.name}"?`)) {
                          del.mutate(s.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {creating && (
        <ShiftFormDialog
          locationId={locationId}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && (
        <ShiftFormDialog
          locationId={locationId}
          initial={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

// =====================================================
// Shift form (create + edit)
// =====================================================

const ShiftFormDialog: React.FC<{
  locationId: string;
  initial?: ShiftDefinition;
  onClose: () => void;
}> = ({ locationId, initial, onClose }) => {
  const create = useCreateShift(locationId);
  const update = useUpdateShift();
  const { data: positions } = usePositions({ limit: 200 });

  const [form, setForm] = useState<ShiftCreateInput & { is_active?: boolean }>({
    code: initial?.code ?? '',
    name: initial?.name ?? '',
    start_time: initial?.start_time ?? '08:00',
    end_time: initial?.end_time ?? '20:00',
    duration_hours: initial?.duration_hours ?? 12,
    position_ids: initial?.position_ids ?? [],
    is_active: initial?.is_active ?? true,
  });
  const [error, setError] = useState('');

  const togglePosition = (pid: string) => {
    setForm((prev) => {
      const cur = new Set(prev.position_ids ?? []);
      if (cur.has(pid)) cur.delete(pid);
      else cur.add(pid);
      return { ...prev, position_ids: Array.from(cur) };
    });
  };

  const submit = async () => {
    setError('');
    try {
      if (initial) {
        await update.mutateAsync({
          id: initial.id,
          data: form,
        });
      } else {
        const { is_active: _ia, ...createData } = form;
        await create.mutateAsync(createData);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    }
  };

  const isPending = create.isPending || update.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogClose onClose={onClose} />
        <DialogHeader>
          <DialogTitle>
            {initial ? 'Редактировать смену' : 'Новая смена'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Код</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="DAY"
              />
            </div>
            <div>
              <Label>Название</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Дневная смена"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Начало</Label>
              <Input
                type="time"
                value={form.start_time}
                onChange={(e) =>
                  setForm({ ...form, start_time: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Конец</Label>
              <Input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
              />
            </div>
            <div>
              <Label>Часов</Label>
              <Input
                type="number"
                step="0.5"
                min="0.5"
                max="24"
                value={form.duration_hours}
                onChange={(e) =>
                  setForm({
                    ...form,
                    duration_hours: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </div>
          </div>

          <div>
            <Label>Допустимые позиции</Label>
            <div className="border rounded-md p-2 max-h-48 overflow-y-auto space-y-1">
              {(positions?.data ?? []).map((p) => (
                <label
                  key={p.id}
                  className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded"
                >
                  <input
                    type="checkbox"
                    checked={(form.position_ids ?? []).includes(p.id)}
                    onChange={() => togglePosition(p.id)}
                  />
                  {p.name}
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Если не выбрано — смена доступна для любой позиции с подходящими
              правилами.
            </p>
          </div>

          {initial && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active ?? true}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
              />
              Активна
            </label>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            {initial ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
